/**
 * viewportEffects.js — Enterprise Visual Effects Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE EFFECTS SUITE]:
 *  - animateWindowLevel: AbortController (защита от двойной анимации)
 *  - [12.0.57] плоскости осей — свои у каждого маппера, во всех объёмных окнах; чужие (вкладка «3D») не снимаются
 *  - _slabStates: Map<vpId> (поддержка множественных вьюпортов)
 *  - animatePresetTransition: очистка overlay при ошибке
 *  - setSlabProjection: BlendModes.COMPOSITE вместо 0
 *  - setVolumeClipping: поддержка параметра viewportId
 *  - applyDualEnergyMode: статический импорт setWindowLevel
 *  - Экспорт SLAB_PRESETS и CLIP_PRESETS
 *  - Curved Planar Reformation (setCPR, applyCPRPreset)
 *  - Volume TF morph (morphTransferFunction)
 *  - Lightbox/Invert (toggleInvert, animateInvert)
 *  - Multi-volume fusion (setFusionMode)
 *  - Flashlight/Spotlight (enableFlashlight, disableFlashlight)
 *  - [12.0.51] Depth cueing удалён: в vtk.js у объёмного маппера его нет
 *  - Silhouette/Edge enhancement (setEdgeEnhancement)
 *  - Chromatic aberration (setChromaticAberration)
 *  - Vignette (setVignette)
 *  - Pseudo-coloring by thickness (setThicknessColoring)
 *  - Slice montage (createSliceMontage)
 *
 * @module viewportEffects
 */

import state, { getRenderingEngine, getViewportIds, find3DViewportId, find3DViewportIds, ViewportType, cornerstone } from './viewportState.js'; // [12.0.57] find3DViewportIds — сечения во все объёмные окна
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { запомнитьПлоскости, стеречьОкно } from './clipGuard.js'; // [12.0.51]
import { setWindowLevel } from './viewportState.js';   // [9.0.0] был круг через viewport.js

// ══════════════════════════════════════════════════════════════
//  SLAB PROJECTIONS (MIP / MinIP / AvgIP)
// ══════════════════════════════════════════════════════════════

/** @type {Map<string, Object>} vpId → slabState */
const _slabStates = new Map();

export const SLAB_PRESETS = Object.freeze({
  'pe-protocol':   { mode: 'mip', thicknessMm: 10, label: 'ТЭЛА (MIP 10mm)' },
  'airway':        { mode: 'minip', thicknessMm: 5, label: 'Бронхи (MinIP 5mm)' },
  'vessel-mip':    { mode: 'mip', thicknessMm: 20, label: 'Сосуды (MIP 20mm)' },
  'interstitium':  { mode: 'avgip', thicknessMm: 7, label: 'Интерстиций (AvgIP 7mm)' },
  'thin-mip':      { mode: 'mip', thicknessMm: 3, label: 'Тонкий MIP 3mm' },
  'thick-mip':     { mode: 'mip', thicknessMm: 30, label: 'Толстый MIP 30mm' },
  'minip-thin':    { mode: 'minip', thicknessMm: 3, label: 'Тонкий MinIP 3mm' },
  'minip-thick':   { mode: 'minip', thicknessMm: 15, label: 'Толстый MinIP 15mm' },
});

export function getSlabPresets() { return { ...SLAB_PRESETS }; }

export function applySlabPreset(viewportId, presetKey) {
  const p = SLAB_PRESETS[presetKey];
  if (!p) return;
  setSlabProjection(viewportId, p.mode, p.thicknessMm);
  showToast(`📐 ${p.label}`, 'info', 1500);
}

/**
 * [12.0.57] Отказ — словами и наружу. Прежде на стопке («Нативные срезы»)
 * функция молча выходила, а кнопка печатала «🎬 MIP 10mm»: на стенде в
 * раскладке MPR 6 кнопки слэба попадали именно в стопку (первое окно), и ни
 * одна проекция не включалась ни разу. Подпись — по-русски, с окном.
 * @returns {{ok:boolean, причина?:string}}
 */
export function setSlabProjection(viewportId, mode = 'mip', thicknessMm = 10) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return { ok: false, причина: 'нет окна' };
  if (vp.type !== ViewportType.ORTHOGRAPHIC) {
    return { ok: false, причина: vp.type === ViewportType.STACK
      ? 'слэб строится в окне реформата (аксиальное, боковое, фронтальное): в стопке «Нативные срезы» каждый снимок — отдельный кадр, толщину ему не задать'
      : 'в объёмном 3D-окне слэба нет — выберите окно реформата' };
  }

  try {
    const B = cornerstone.Enums?.BlendModes;

    if (mode === 'off') {
      vp.setSlabThickness?.(0.1);
      vp.setBlendMode?.(B?.COMPOSITE || 0);
      _slabStates.delete(viewportId);
      vp.render();
      showToast(`Слэб выключен — обычный тонкий срез · окно «${viewportId}»`, 'info', 1500);
      events.emit(EVENTS.SLICE_PROJECTION_CHANGED, { viewportId, mode: 'off', thickness: 0 });
      return { ok: true };
    }

    const blendModeMap = { mip: B?.MAXIMUM_INTENSITY_BLEND || 1, minip: B?.MINIMUM_INTENSITY_BLEND || 2, avgip: B?.AVERAGE_INTENSITY_BLEND || 3 };
    const blendMode = blendModeMap[mode] ?? 1;

    vp.setSlabThickness?.(thicknessMm);
    vp.setBlendMode?.(blendMode);
    vp.render();

    _slabStates.set(viewportId, { vpId: viewportId, mode, thickness: thicknessMm });
    const modeLabel = { mip: 'MIP (максимум)', minip: 'MinIP (минимум)', avgip: 'Среднее' }[mode] || mode;
    showToast(`Слэб ${modeLabel}, ${thicknessMm} мм · окно «${viewportId}»`, 'info', 1500);
    events.emit(EVENTS.SLICE_PROJECTION_CHANGED, { viewportId, mode, thickness: thicknessMm });
    return { ok: true };
  } catch (e) { log.warn('[Slab]', e.message); return { ok: false, причина: e.message }; }
}

export function getSlabState(viewportId) {
  const st = _slabStates.get(viewportId);
  return st ? { ...st } : null;
}

export function cycleSlabProjection(viewportId, thicknessMm = 10) {
  const current = _slabStates.get(viewportId);
  const cycle = ['off', 'mip', 'minip', 'avgip'];
  return setSlabProjection(viewportId, cycle[(cycle.indexOf(current?.mode || 'off') + 1) % cycle.length], thicknessMm);   // [12.0.57] ответ — наружу
}

export function adjustSlabThickness(viewportId, delta) {
  const st = _slabStates.get(viewportId);
  if (!st) return;
  const newT = Math.max(1, Math.min(100, st.thickness + delta));
  setSlabProjection(viewportId, st.mode, newT);
}

// ══════════════════════════════════════════════════════════════
//  CURVED PLANAR REFORMATION (CPR)
// ══════════════════════════════════════════════════════════════

const CPR_PRESETS = Object.freeze({
  'vessel-straight': { halfWidth: 15, sampleDist: 0.5, label: 'Сосуд (прямая CPR)' },
  'vessel-stretched': { halfWidth: 20, sampleDist: 1, label: 'Сосуд (растянутая CPR)' },
  'airway': { halfWidth: 12, sampleDist: 0.5, label: 'Бронх' },
  'colon': { halfWidth: 25, sampleDist: 1, label: 'Толстая кишка' },
});

export function getCPRPresets() { return { ...CPR_PRESETS }; }

/**
 * Построить Curved Planar Reformation вдоль центральной линии.
 * Делегирует в vtkModule.buildCPR.
 *
 * @param {string} viewportId
 * @param {number[][]} centerline — массив точек [x, y, z] в мировых координатах
 * @param {Object} [opts]
 * @param {number} [opts.halfWidth=20] — полуширина в мм
 * @param {number} [opts.sampleDist=1] — шаг дискретизации в мм
 * @returns {Promise<Object|null>}
 */
export async function setCPR(viewportId, centerline, opts = {}) {
  try {
    const vp = getRenderingEngine()?.getViewport(viewportId);
    if (!vp) return null;

    const halfWidth = opts.halfWidth || 20;
    const sampleDist = opts.sampleDist || 1;

    // Получаем пиксельные данные объёма
    const actors = vp.getActors?.();
    if (!actors?.length) return null;

    const volId = actors[0]?.uid || actors[0]?.referenceId;
    let vol = null;
    try { vol = cornerstone.cache.getVolume?.(volId); } catch (e) {}

    let sd = null;
    try { sd = vol?.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) {}
    if (!sd && vol?.imageData) try { sd = vol.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) {}
    if (!sd) try { sd = vol?.getScalarData?.(); } catch (e) {}

    if (!sd) return null;

    const dims = vol?.dimensions || [512, 512, 1];
    const spacing = vol?.spacing || [1, 1, 1];

    // Пробуем vtkModule
    try {
      const { buildCPR } = await import('../compute/vtkModule.js');
      if (typeof buildCPR === 'function') {
        const result = buildCPR(sd, dims, spacing, centerline, halfWidth, sampleDist);
        if (result?.success) {
          showToast('📐 CPR построена', 'success', 2000);
          return result;
        }
      }
    } catch (e) { log.debug('[CPR] vtkModule:', e.message); }

    return { success: false, error: 'CPR requires vtkModule.js' };
  } catch (e) { log.warn('[CPR]', e.message); return null; }
}

export function applyCPRPreset(viewportId, centerline, presetKey) {
  const p = CPR_PRESETS[presetKey];
  if (!p) return;
  return setCPR(viewportId, centerline, { halfWidth: p.halfWidth, sampleDist: p.sampleDist });
}

// ══════════════════════════════════════════════════════════════
//  CLIPPING PLANES
// ══════════════════════════════════════════════════════════════

let _clipValues = { x: 0, y: 0, z: 0 };
// [10.19.30] Выключатели осей. До этого выключатели у ползунков отсечения были
// украшением: щёлкали классом и НИЧЕГО не отключали — плоскость ставилась по
// одному значению ползунка. Теперь ось режет, только когда она ВКЛЮЧЕНА и
// значение больше нуля. Явная передача значения в setVolumeClipping включает
// ось сама — иначе сломались бы пресеты (applyClipPreset) и «Удалить стол»,
// которые зовут функцию напрямую и о выключателях не знают.
let _clipEnabled = { x: true, y: true, z: true };
let _vtkPlanePromise = null;

function _getVtkPlane() {
  if (!_vtkPlanePromise) _vtkPlanePromise = import('@kitware/vtk.js/Common/DataModel/Plane').then(m => m.default || m).catch(() => null);
  return _vtkPlanePromise;
}
_getVtkPlane();

export const CLIP_PRESETS = Object.freeze({
  'cardiac-anterior': { x: 0, y: 40, z: 0, label: 'Кардио: передняя стенка' },
  'cardiac-lateral':  { x: 35, y: 0, z: 0, label: 'Кардио: боковая стенка' },
  'spine-anterior':   { x: 0, y: 55, z: 0, label: 'Позвоночник: передняя часть' },
  'abdomen-upper':    { x: 0, y: 0, z: 40, label: 'Живот: верхний этаж' },
  'head-sagittal':    { x: 50, y: 0, z: 0, label: 'Голова: сагиттальный срез' },
  'head-coronal':     { x: 0, y: 50, z: 0, label: 'Голова: корональный срез' },
  'reset':            { x: 0, y: 0, z: 0, label: 'Сброс' },
});

export function getClipPresets() { return { ...CLIP_PRESETS }; }

export function applyClipPreset(viewportId, presetKey) {
  const p = CLIP_PRESETS[presetKey];
  if (!p) return;
  setVolumeClipping(p.x, p.y, p.z, viewportId);
  showToast(`✂ ${p.label}`, 'info', 1500);
}

/**
 * [12.0.57] ПЛОСКОСТИ ОСЕЙ — СВОИ У КАЖДОГО МАППЕРА, ЧУЖИЕ НЕ ТРОГАЮТСЯ.
 *
 * Найдено на стенде «Спец. режимы» и чтением соседа. Прежде здесь было:
 *   • плоскость ложилась только в ПЕРВОЕ объёмное окно (find3DViewportId).
 *     В «MPR 6» объёмных окон два, и во втором («3D · пресет») сечение не
 *     появлялось — ровно та яма, из которой отсечение стола вытащили в
 *     11.1.36 («плоскость ложилась в окно режима 3D, а 3D-пресет — ДРУГОЕ
 *     окно»);
 *   • перед своими плоскостями звался mapper.removeAllClippingPlanes(): сечение
 *     по X снимало «Стол (сзади)», «Переднюю стенку» и «Окно обзора» вкладки
 *     «3D» — врач убрал стол, сдвинул сечение, стол вернулся. И наоборот:
 *     тумблер стола сносил сечения осей.
 * Теперь: плоскости осей — свои у каждого маппера (WeakMap), чужие плоскости
 * остаются на месте, и вместе их не больше шести (столько держит шейдер vtk.js,
 * см. 11.1.49 в sidebarBindings). Не влезло — честная причина в ответе.
 *
 * @returns {Promise<{окон:number, сечений:number, причина:string|null}>}
 */
const _плоскостиОсей = new WeakMap();   // маппер → {x, y, z} (vtkPlane)

/** Плоскости осей, которые сейчас стоят на маппере (для соседа-отсечения). */
export function плоскостиОсейНаМаппере(mapper) {
  const свои = mapper && _плоскостиОсей.get(mapper);
  if (!свои) return [];
  const набор = new Set([свои.x, свои.y, свои.z]);
  return (mapper.getClippingPlanes?.() || []).filter((п) => набор.has(п));
}

function _объёмныеАктёры(viewportId) {
  const re = getRenderingEngine();
  const ids = [...new Set([...(find3DViewportIds() || []), ...(viewportId ? [viewportId] : [])])];
  const out = [];
  for (const id of ids) {
    const vp = re?.getViewport(id);
    if (!vp || vp.type !== ViewportType.VOLUME_3D) continue;
    const актёр = vp.getDefaultActor?.()?.actor || vp.getActors?.()?.[0]?.actor;
    const mapper = актёр?.getMapper?.();
    if (!mapper?.addClippingPlane || !mapper.getClippingPlanes) continue;
    out.push({ vp, актёр, mapper });
  }
  return out;
}

export async function setVolumeClipping(xPct, yPct, zPct, viewportId) {
  // Явное значение = намерение резать по этой оси: включаем её. [10.19.30]
  if (xPct != null) { _clipValues.x = Number(xPct); _clipEnabled.x = true; }
  if (yPct != null) { _clipValues.y = Number(yPct); _clipEnabled.y = true; }
  if (zPct != null) { _clipValues.z = Number(zPct); _clipEnabled.z = true; }

  const актёры = _объёмныеАктёры(viewportId);
  if (!актёры.length) return { окон: 0, сечений: 0, причина: 'нет объёмного окна — откройте раскладку с 3D (например, MPR 6)' };
  const vtkPlane = await _getVtkPlane();
  if (!vtkPlane) return { окон: 0, сечений: 0, причина: 'плоскости отсечения недоступны в этой сборке' };

  let окон = 0, сечений = 0, причина = null;
  for (const { vp, актёр, mapper } of актёры) {
    const bounds = актёр.getBounds?.();
    if (!bounds || bounds.length < 6) continue;
    let свои = _плоскостиОсей.get(mapper);
    if (!свои) { свои = { x: vtkPlane.newInstance(), y: vtkPlane.newInstance(), z: vtkPlane.newInstance() }; _плоскостиОсей.set(mapper, свои); }
    const набор = new Set([свои.x, свои.y, свои.z]);
    const чужие = mapper.getClippingPlanes().filter((п) => !набор.has(п));
    const [xMin, xMax, yMin, yMax, zMin, zMax] = bounds;
    const нужны = [];
    if (_clipEnabled.x && _clipValues.x > 0) {
      свои.x.setOrigin(xMin + (xMax - xMin) * _clipValues.x / 100, 0, 0); свои.x.setNormal(-1, 0, 0); нужны.push(свои.x);
    }
    if (_clipEnabled.y && _clipValues.y > 0) {
      свои.y.setOrigin(0, yMin + (yMax - yMin) * _clipValues.y / 100, 0); свои.y.setNormal(0, -1, 0); нужны.push(свои.y);
    }
    if (_clipEnabled.z && _clipValues.z > 0) {
      свои.z.setOrigin(0, 0, zMin + (zMax - zMin) * _clipValues.z / 100); свои.z.setNormal(0, 0, -1); нужны.push(свои.z);
    }
    const мест = Math.max(0, 6 - чужие.length);
    if (нужны.length > мест) {
      причина = `шейдер объёма держит не больше шести плоскостей, а ${чужие.length} уже занято отсечением вкладки «3D» (стол, передняя стенка, окно обзора)`;
    }
    mapper.removeAllClippingPlanes();
    for (const п of чужие) mapper.addClippingPlane(п);
    for (const п of нужны.slice(0, мест)) mapper.addClippingPlane(п);
    сечений = Math.max(сечений, Math.min(нужны.length, мест));
    // [12.0.51] Сторож: Cornerstone при повороте вида переставляет первые две
    // плоскости маппера в слой у фокуса (подробно — core/clipGuard.js).
    // [12.0.57] Помним ВЕСЬ список маппера — и свои, и соседские.
    запомнитьПлоскости(mapper, mapper.getClippingPlanes().map((п) => ({ origin: п.getOrigin(), normal: п.getNormal() })));
    стеречьОкно(vp.element);
    vp.render();
    окон++;
  }
  state.cinematicRenderer?.forceRender();
  return { окон, сечений, причина };
}

/**
 * Включить или выключить отсечение по одной оси, НЕ трогая значение ползунка.
 * [10.19.30] Именно этого выключателям и не хватало: раньше они щёлкали
 * классом в разметке, а плоскость оставалась. Выключение оси перечитывает
 * набор плоскостей — срез по этой оси снимается сразу, значение сохраняется,
 * и повторное включение возвращает срез на то же место.
 * @param {'x'|'y'|'z'} axis
 * @param {boolean} on
 */
export function setClipAxisEnabled(axis, on) {
  if (!(axis in _clipEnabled)) return null;
  _clipEnabled[axis] = !!on;
  // Пересобрать плоскости с текущими значениями: null = «значения не менять».
  return setVolumeClipping(null, null, null);   // [12.0.57] ответ — вызывающему
}

export function getClipAxisEnabled() { return { ..._clipEnabled }; }

export function getVolumeClipping() { return { ..._clipValues }; }

export function resetClipping() {
  // [12.0.57] Свои плоскости снимаются С МАППЕРОВ, чужие (вкладка «3D») —
  // остаются. Прежде здесь удалялись объекты плоскостей, а на маппере они
  // оставались висеть — уже удалёнными.
  for (const { vp, mapper } of _объёмныеАктёры()) {
    try {
      const наши = new Set(плоскостиОсейНаМаппере(mapper));
      if (!наши.size) continue;
      const чужие = mapper.getClippingPlanes().filter((п) => !наши.has(п));
      mapper.removeAllClippingPlanes();
      for (const п of чужие) mapper.addClippingPlane(п);
      запомнитьПлоскости(mapper, чужие.map((п) => ({ origin: п.getOrigin(), normal: п.getNormal() })));
      vp.render();
    } catch (e) { log.debug('[Отсечение] сброс:', e.message); }
  }
  _clipValues = { x: 0, y: 0, z: 0 };
  _clipEnabled = { x: true, y: true, z: true }; // [10.19.30] сброс возвращает и оси
}

// ══════════════════════════════════════════════════════════════
//  АВТОВРАЩЕНИЕ 3D [10.19.30]
//
//  Выключатели «Автовращение» и «Поворотный» в спец-режимах были украшением:
//  щёлкали классом и ничего не крутили. Теперь автовращение настоящее —
//  камера объёмного окна облетает точку фокуса. «Поворотный» задаёт ХАРАКТЕР
//  облёта: включён — гончарный круг (ось мира Z, горизонт не заваливается,
//  так смотрят кости и сосуды), выключен — свободная орбита вокруг текущей
//  вертикали камеры (как продолжение ручного вращения мышью).
//
//  Скорость привязана к часам, а не к кадрам: 18°/с и на 30, и на 144 Гц.
// ══════════════════════════════════════════════════════════════

let _spinRaf = 0;
let _spinPrevTs = 0;
let _spinTurntable = false;
const SPIN_DEG_PER_SEC = 18;

/** Поворот точки p вокруг оси axis (единичной) на угол rad относительно c — формула Родрига. */
function _rotateAround(p, c, axis, rad) {
  const [px, py, pz] = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
  const [ux, uy, uz] = axis;
  const cos = Math.cos(rad), sin = Math.sin(rad), dot = ux * px + uy * py + uz * pz;
  return [
    c[0] + px * cos + (uy * pz - uz * py) * sin + ux * dot * (1 - cos),
    c[1] + py * cos + (uz * px - ux * pz) * sin + uy * dot * (1 - cos),
    c[2] + pz * cos + (ux * py - uy * px) * sin + uz * dot * (1 - cos),
  ];
}

function _norm(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

function _spinStep(ts) {
  const vpId = find3DViewportId();
  const vp = vpId && getRenderingEngine()?.getViewport(vpId);
  if (!vp) { stopAutoSpin(); return; } // объёмное окно исчезло — честно останавливаемся
  const dt = _spinPrevTs ? Math.min((ts - _spinPrevTs) / 1000, 0.1) : 0;
  _spinPrevTs = ts;
  if (dt > 0) {
    try {
      const cam = vp.getCamera();
      const axis = _spinTurntable ? [0, 0, 1] : _norm(cam.viewUp || [0, 0, 1]);
      const rad = (SPIN_DEG_PER_SEC * Math.PI / 180) * dt;
      const position = _rotateAround(cam.position, cam.focalPoint, axis, rad);
      const next = { ...cam, position };
      if (_spinTurntable) next.viewUp = [0, 0, 1]; // гончарный круг держит горизонт
      else next.viewUp = _rotateAround(
        [cam.focalPoint[0] + cam.viewUp[0], cam.focalPoint[1] + cam.viewUp[1], cam.focalPoint[2] + cam.viewUp[2]],
        cam.focalPoint, axis, rad,
      ).map((v, i) => v - cam.focalPoint[i]);
      vp.setCamera(next);
      vp.render();
      state.cinematicRenderer?.forceRender();
    } catch (e) { log.debug('[AutoSpin]', e.message); }
  }
  _spinRaf = requestAnimationFrame(_spinStep);
}

/**
 * Запустить автовращение. Возвращает true, если объёмное окно нашлось и
 * вращение пошло; false — если крутить нечего (пусть кнопка скажет честно).
 */
export function startAutoSpin() {
  if (_spinRaf) return true;
  if (!find3DViewportId()) return false;
  _spinPrevTs = 0;
  _spinRaf = requestAnimationFrame(_spinStep);
  return true;
}

export function stopAutoSpin() {
  if (_spinRaf) cancelAnimationFrame(_spinRaf);
  _spinRaf = 0;
  _spinPrevTs = 0;
}

export function isAutoSpinOn() { return _spinRaf !== 0; }

/** «Поворотный»: true — гончарный круг (ось Z мира), false — свободная орбита. */
export function setAutoSpinTurntable(on) { _spinTurntable = !!on; }
export function getAutoSpinTurntable() { return _spinTurntable; }

// ══════════════════════════════════════════════════════════════
//  ANIMATED W/L TRANSITION (с AbortController)
// ══════════════════════════════════════════════════════════════

const _wlAnimations = new Map();

export function animateWindowLevel(viewportId, targetWW, targetWC, duration = 500) {
  // Отменяем предыдущую анимацию
  const prev = _wlAnimations.get(viewportId);
  if (prev) { prev.abort(); _wlAnimations.delete(viewportId); }

  return new Promise(resolve => {
    const vp = getRenderingEngine()?.getViewport(viewportId);
    if (!vp) { resolve(); return; }

    let curWW, curWC;
    try {
      const r = vp.getProperties?.()?.voiRange;
      if (r) { curWW = r.upper - r.lower; curWC = (r.upper + r.lower) / 2; }
    } catch (e) { log.debug("[viewportEffects]", e.message); }

    if (curWW == null) { curWW = targetWW; curWC = targetWC; }

    const ac = new AbortController();
    _wlAnimations.set(viewportId, ac);

    const t0 = performance.now();
    const dWW = targetWW - curWW;
    const dWC = targetWC - curWC;
    const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (now) => {
      if (ac.signal.aborted) { _wlAnimations.delete(viewportId); resolve(); return; }

      const t = Math.min((now - t0) / duration, 1);
      const e = ease(t);

      try {
        const ww = curWW + dWW * e;
        const wc = curWC + dWC * e;
        const { lower, upper } = cornerstone.utilities.windowLevel.toLowHighRange(ww, wc);
        vp.setProperties({ voiRange: { lower, upper } });
        vp.render();
      } catch (e) { log.debug("[viewportEffects]", e.message); }

      if (t < 1) requestAnimationFrame(step);
      else { _wlAnimations.delete(viewportId); resolve(); }
    };
    requestAnimationFrame(step);
  });
}

// ══════════════════════════════════════════════════════════════
//  ANIMATED 3D PRESET TRANSITION (fade-through-black)
// ══════════════════════════════════════════════════════════════

export async function animatePresetTransition(presetName, duration = 800, viewportId) {
  let overlay = null;
  try {
    const { set3DPreset } = await import('./viewport.js');
    const vpId = viewportId || find3DViewportId();
    const vp = getRenderingEngine()?.getViewport(vpId);
    if (!vp || vp.type !== ViewportType.VOLUME_3D) { set3DPreset(presetName, vpId); return; }
    const el = vp.element;
    if (!el) { set3DPreset(presetName, vpId); return; }

    overlay = document.createElement('div');
    overlay.style.cssText = `position:absolute;inset:0;background:#000;opacity:0;transition:opacity ${duration / 2}ms;z-index:200;pointer-events:none;`;
    el.appendChild(overlay);
    requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    await new Promise(r => setTimeout(r, duration / 2 + 50));
    set3DPreset(presetName, vpId);
    overlay.style.opacity = '0';
    await new Promise(r => setTimeout(r, duration / 2 + 50));
  } catch (e) {
    log.warn(`[viewportEffects] animatePresetTransition failed:`, e.message);
  } finally {
    // Гарантированная очистка overlay
    try { overlay?.remove(); } catch {}
  }
}

// ══════════════════════════════════════════════════════════════
//  DUAL-ENERGY SIMULATION
// ══════════════════════════════════════════════════════════════

export const DUAL_ENERGY_MODES = Object.freeze({
  'virtual-non-contrast': { ww: 200, wc: 50, label: 'Виртуальный нативный', description: 'Имитация снимка без контраста' },
  'iodine-map':           { ww: 100, wc: 150, label: 'Карта йода', description: 'Визуализация контрастного вещества' },
  'calcium-suppress':     { ww: 400, wc: 40, label: 'Подавление кальция', description: 'Подавление кальцинатов' },
  'bone-remove':          { ww: 350, wc: 50, label: 'Удаление костей', description: 'Мягкие ткани без костей' },
  'lung-perfusion':       { ww: 600, wc: -400, label: 'Перфузия лёгких', description: 'Контраст в лёгочной ткани' },
  'soft-tissue':          { ww: 400, wc: 40, label: 'Мягкие ткани', description: 'Оптимизация для мягких тканей' },
});

export function getDualEnergyModes() { return { ...DUAL_ENERGY_MODES }; }

export async function applyDualEnergyMode(viewportId, mode, animate = true) {
  try {
    const m = DUAL_ENERGY_MODES[mode];
    if (!m) return;

    if (animate) {
      await animateWindowLevel(viewportId, m.ww, m.wc, 400);
    } else {
      setWindowLevel(viewportId, m.ww, m.wc);
    }

    showToast(`🔬 ${m.label}`, 'info', 2000);
    events.emit(EVENTS.DUAL_ENERGY_APPLIED, { viewportId, mode, ...m });
  } catch (e) { log.warn(`[viewportEffects] applyDualEnergyMode failed:`, e.message); }
}

// ══════════════════════════════════════════════════════════════
//  VOLUME TF MORPH (плавный переход между transfer functions)
// ══════════════════════════════════════════════════════════════

/**
 * Плавно перейти от текущей TF к целевой.
 * @param {string} viewportId — 3D viewport
 * @param {Object} targetTF — { color, opacity, gradient, shade, ambient, diffuse, specular, specularPower }
 * @param {number} [duration=1000]
 */
export async function morphTransferFunction(viewportId, targetTF, duration = 1000) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp || vp.type !== ViewportType.VOLUME_3D) return;

  const prop = vp.getDefaultActor?.()?.actor?.getProperty?.();
  if (!prop) return;

  // Сохраняем текущую TF
  const ofun = prop.getScalarOpacity(0);
  const cfun = prop.getRGBTransferFunction(0);

  const startOpacity = [];
  const startColor = [];
  for (let i = -1024; i <= 3071; i += 100) {
    startOpacity.push({ hu: i, alpha: ofun.getValue(i) });
    const rgb = [0, 0, 0];
    cfun.getColor(i, rgb);
    startColor.push({ hu: i, r: rgb[0], g: rgb[1], b: rgb[2] });
  }

  const t0 = performance.now();
  const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  return new Promise(resolve => {
    const step = (now) => {
      const t = Math.min((now - t0) / duration, 1);
      const e = ease(t);

      ofun.removeAllPoints();
      cfun.removeAllPoints();

      // Интерполируем opacity
      for (const sp of startOpacity) {
        const targetAlpha = _getTFValue(targetTF.opacity || [], sp.hu);
        const alpha = sp.alpha + (targetAlpha - sp.alpha) * e;
        if (alpha > 0.001) ofun.addPoint(sp.hu, alpha);
      }

      // Интерполируем color
      for (const sc of startColor) {
        const targetRgb = _getTFColor(targetTF.color || [], sc.hu);
        const r = sc.r + (targetRgb.r - sc.r) * e;
        const g = sc.g + (targetRgb.g - sc.g) * e;
        const b = sc.b + (targetRgb.b - sc.b) * e;
        cfun.addRGBPoint(sc.hu, r, g, b);
      }

      // Shading
      if (targetTF.shade != null) {
        const s = (prop.getShade?.() ?? true);
        prop.setShade(e > 0.5 ? targetTF.shade : s);
      }

      vp.render();

      if (t < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

function _getTFValue(points, hu) {
  if (!points?.length) return 0;
  if (hu <= points[0][0]) return points[0][1];
  if (hu >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    if (hu >= points[i][0] && hu <= points[i + 1][0]) {
      const t = (hu - points[i][0]) / (points[i + 1][0] - points[i][0]);
      return points[i][1] + (points[i + 1][1] - points[i][1]) * t;
    }
  }
  return 0;
}

function _getTFColor(points, hu) {
  if (!points?.length) return { r: 1, g: 1, b: 1 };
  if (hu <= points[0][0]) return { r: points[0][1], g: points[0][2], b: points[0][3] };
  if (hu >= points[points.length - 1][0]) return { r: points[points.length - 1][1], g: points[points.length - 1][2], b: points[points.length - 1][3] };
  for (let i = 0; i < points.length - 1; i++) {
    if (hu >= points[i][0] && hu <= points[i + 1][0]) {
      const t = (hu - points[i][0]) / (points[i + 1][0] - points[i][0]);
      return {
        r: points[i][1] + (points[i + 1][1] - points[i][1]) * t,
        g: points[i][2] + (points[i + 1][2] - points[i][2]) * t,
        b: points[i][3] + (points[i + 1][3] - points[i][3]) * t,
      };
    }
  }
  return { r: 1, g: 1, b: 1 };
}

// ══════════════════════════════════════════════════════════════
//  LIGHTBOX / INVERT
// ══════════════════════════════════════════════════════════════

const _invertStates = new Map();

export function toggleInvert(viewportId) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return;

  const isInverted = _invertStates.get(viewportId) || false;
  const newState = !isInverted;
  _invertStates.set(viewportId, newState);

  try {
    const props = vp.getProperties?.();
    if (props?.voiRange) {
      const { lower, upper } = props.voiRange;
      // Инвертирование с сохранением ширины окна
      if (newState) {
        // Сохраняем оригинальные значения
        _invertStates.set(viewportId, { inverted: true, original: { lower, upper } });
        vp.setProperties({ voiRange: { lower: upper, upper: lower } });
      } else {
        const original = _invertStates.get(viewportId)?.original;
        if (original) {
          vp.setProperties({ voiRange: { lower: original.lower, upper: original.upper } });
        }
      }
      vp.render();
      showToast(newState ? '🔄 Инвертировано' : '🔄 Норма', 'info', 1200);
    }
  } catch (e) { log.warn('[Invert]', e.message); }
}

export async function animateInvert(viewportId, duration = 500) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return;

  const props = vp.getProperties?.();
  if (!props?.voiRange) return;

  const { lower, upper } = props.voiRange;
  await animateWindowLevel(viewportId, upper - lower, -(lower + upper) / 2, duration);
  _invertStates.set(viewportId, true);
}

// ══════════════════════════════════════════════════════════════
//  MULTI-VOLUME FUSION
// ══════════════════════════════════════════════════════════════

const FUSION_COLORMAPS = {
  'pet-rainbow': [{ hu: 0, r: 0, g: 0, b: 1 }, { hu: 100, r: 0, g: 1, b: 0 }, { hu: 200, r: 1, g: 1, b: 0 }, { hu: 300, r: 1, g: 0, b: 0 }],
  'pet-hot': [{ hu: 0, r: 0, g: 0, b: 0 }, { hu: 100, r: 1, g: 0.5, b: 0 }, { hu: 200, r: 1, g: 1, b: 0 }, { hu: 300, r: 1, g: 1, b: 1 }],
  'pet-cool': [{ hu: 0, r: 0, g: 0, b: 1 }, { hu: 100, r: 0, g: 1, b: 1 }, { hu: 200, r: 0.5, g: 1, b: 0.5 }, { hu: 300, r: 1, g: 1, b: 1 }],
  'perfusion': [{ hu: 0, r: 0, g: 0, b: 0.5 }, { hu: 100, r: 0, g: 0.5, b: 1 }, { hu: 200, r: 0, g: 1, b: 0.5 }, { hu: 300, r: 1, g: 1, b: 0 }],
};

export function setFusionMode(viewportId, colormapName, opacity = 0.5) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp || vp.type === ViewportType.STACK) return;

  const colormap = FUSION_COLORMAPS[colormapName];
  if (!colormap) return;

  try {
    const actors = vp.getActors?.();
    if (!actors?.length) return;

    // Применяем colormap ко второму актору (overlay) если есть
    if (actors.length > 1) {
      const prop = actors[1].actor?.getProperty?.();
      if (prop) {
        const cfun = prop.getRGBTransferFunction(0);
        cfun.removeAllPoints();
        for (const pt of colormap) {
          cfun.addRGBPoint(pt.hu, pt.r, pt.g, pt.b);
        }
        const ofun = prop.getScalarOpacity(0);
        ofun.removeAllPoints();
        ofun.addPoint(0, 0);
        ofun.addPoint(1, opacity);
        vp.render();
        showToast(`🎨 Палитра слияния: ${colormapName}`, 'info', 1500); // [10.19.30] «фьюжн» переведён
      }
    }
  } catch (e) { log.warn('[Fusion]', e.message); }
}

// ══════════════════════════════════════════════════════════════
//  FLASHLIGHT / SPOTLIGHT
// ══════════════════════════════════════════════════════════════

const _flashlightStates = new Map();

/**
 * Включить эффект фонарика (освещение области вокруг курсора).
 * @param {string} viewportId
 * @param {number} [radius=100] — радиус в пикселях
 */
export function enableFlashlight(viewportId, radius = 100) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp?.element) return;

  disableFlashlight(viewportId);

  const overlay = document.createElement('div');
  overlay.className = 'flashlight-overlay';
  overlay.style.cssText = `
    position:absolute; inset:0; pointer-events:none; z-index:50;
    background:radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.85) 70%);
    transition: none;
  `;
  vp.element.appendChild(overlay);

  const handler = (evt) => {
    const rect = vp.element.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    overlay.style.background = `radial-gradient(circle ${radius}px at ${x}px ${y}px, transparent 0%, rgba(0,0,0,0.85) 70%)`;
  };

  vp.element.addEventListener('pointermove', handler);
  _flashlightStates.set(viewportId, { overlay, handler, radius });
}

export function disableFlashlight(viewportId) {
  const st = _flashlightStates.get(viewportId);
  if (!st) return;
  const vp = getRenderingEngine()?.getViewport(viewportId);
  vp?.element?.removeEventListener('pointermove', st.handler);
  st.overlay.remove();
  _flashlightStates.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  DEPTH CUEING / FOG
// ══════════════════════════════════════════════════════════════
// [12.0.51] setDepthCueing УДАЛЕНА вместе с кнопкой «Туман». Она звала
// mapper.setDepthCueing — в vtk.js 36 (на нём стоит Cornerstone 5.6.3) такого
// метода у объёмного маппера НЕТ (сверено по исходникам vtk.js и на стенде:
// typeof mapper.setDepthCueing === 'undefined'). Условие молча не выполнялось,
// а кнопка печатала «Дальние структуры притемнены». Настоящий туман есть в
// кинематографическом рендере этой же вкладки («Объёмный туман»).

// ══════════════════════════════════════════════════════════════
//  SILHOUETTE / EDGE ENHANCEMENT — прозрачность по градиенту
// ══════════════════════════════════════════════════════════════
// [12.0.51] Переписано. Было: setUseGradientOpacity(0,true) и кривая через
// prop.getGradientOpacity(0) — такого метода в vtk.js нет, кривая не ставилась,
// а включённая прозрачность по градиенту работала с заводским диапазоном
// 0…1 (почти любой перепад плотности давал полную непрозрачность) — на кадре
// почти ничего не менялось. Выключить было нечем. Теперь — настоящие числа
// диапазона (vtk.js VolumeProperty: gradientOpacityMinimum/MaximumValue и
// …Opacity) и выключение, возвращающее прежние значения.
const _краяБыло = new Map();   // viewportId → прежние настройки свойства

/** Края есть? */
export function isEdgeEnhancementOn(viewportId) { return _краяБыло.has(viewportId); }

/**
 * @param {string} viewportId
 * @param {boolean} enabled
 * @param {{от?:number, до?:number}} [диапазон] — перепад плотности (HU на шаг
 *   выборки), ниже «от» вещество прозрачно, выше «до» — непрозрачно как было
 * @returns {boolean} получилось ли
 */
export function setEdgeEnhancement(viewportId, enabled = true, диапазон = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const prop = vp?.getDefaultActor?.()?.actor?.getProperty?.() || vp?.getActors?.()?.[0]?.actor?.getProperty?.();
  if (!prop?.setUseGradientOpacity || !prop.setGradientOpacityMinimumValue) return false;
  try {
    if (enabled) {
      if (!_краяБыло.has(viewportId)) {
        _краяБыло.set(viewportId, {
          use: prop.getUseGradientOpacity(0),
          minV: prop.getGradientOpacityMinimumValue(0), maxV: prop.getGradientOpacityMaximumValue(0),
          minO: prop.getGradientOpacityMinimumOpacity(0), maxO: prop.getGradientOpacityMaximumOpacity(0),
        });
      }
      prop.setGradientOpacityMinimumValue(0, диапазон.от ?? 10);
      prop.setGradientOpacityMaximumValue(0, диапазон.до ?? 150);
      prop.setGradientOpacityMinimumOpacity(0, 0);
      prop.setGradientOpacityMaximumOpacity(0, 1);
      prop.setUseGradientOpacity(0, true);
    } else {
      const б = _краяБыло.get(viewportId);
      _краяБыло.delete(viewportId);
      if (б) {
        prop.setGradientOpacityMinimumValue(0, б.minV); prop.setGradientOpacityMaximumValue(0, б.maxV);
        prop.setGradientOpacityMinimumOpacity(0, б.minO); prop.setGradientOpacityMaximumOpacity(0, б.maxO);
        prop.setUseGradientOpacity(0, б.use);
      } else {
        prop.setUseGradientOpacity(0, false);
      }
    }
    vp.render();
    state.cinematicRenderer?.forceRender?.();
    return true;
  } catch (e) { log.warn('[Edge]', e.message); return false; }
}

// ══════════════════════════════════════════════════════════════
//  CHROMATIC ABERRATION
// ══════════════════════════════════════════════════════════════
// [12.0.51] Переписано. Фильтр стоял ссылкой на data:-адрес SVG внутри CSS
// `filter: url("data:image/svg+xml,…#chroma")`. Chromium (а значит и Electron)
// такие внешние ссылки на фильтр не исполняет: замер на стенде — средние
// R/G/B кадра до и после «включения» совпали до десятых. Кроме того, сама
// формула склеивала каналы через feMerge поверх непрозрачного исходника, и
// при работающем фильтре получился бы один красный канал. Теперь фильтр
// лежит в самом документе (url(#id) Chromium исполняет), каналы разнесены
// честно и сложены: R сдвинут вправо, B влево, G на месте.

const _chromaticStates = new Map();
const _ХРОМА_ИД = 'mk-chroma-filter';

function _фильтрХромы(сдвиг) {
  let svg = document.getElementById(_ХРОМА_ИД + '-svg');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = _ХРОМА_ИД + '-svg';
    svg.setAttribute('width', '0'); svg.setAttribute('height', '0');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
    document.body.appendChild(svg);
  }
  svg.innerHTML = `<filter id="${_ХРОМА_ИД}" color-interpolation-filters="sRGB" x="0" y="0" width="100%" height="100%">`
    + `<feOffset in="SourceGraphic" dx="${сдвиг}" dy="0" result="rs"/>`
    + `<feColorMatrix in="rs" type="matrix" values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="r"/>`
    + `<feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0" result="g"/>`
    + `<feOffset in="SourceGraphic" dx="${-сдвиг}" dy="0" result="bs"/>`
    + `<feColorMatrix in="bs" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0" result="b"/>`
    + `<feComposite in="r" in2="g" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="rg"/>`
    + `<feComposite in="rg" in2="b" operator="arithmetic" k1="0" k2="1" k3="1" k4="0"/>`
    + '</filter>';
}

/** @returns {boolean} получилось ли */
export function setChromaticAberration(viewportId, intensity = 2) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp?.element) return false;
  disableChromaticAberration(viewportId);
  const canvas = vp.canvas || vp.element.querySelector('canvas');
  if (!canvas) return false;
  _фильтрХромы(Math.max(1, Math.round(intensity)));
  canvas.style.filter = `url(#${_ХРОМА_ИД})`;
  _chromaticStates.set(viewportId, { canvas, intensity });
  return true;
}

export function disableChromaticAberration(viewportId) {
  const st = _chromaticStates.get(viewportId);
  if (st) {
    st.canvas.style.filter = '';
    _chromaticStates.delete(viewportId);
  }
}

export function isChromaticAberrationOn(viewportId) { return _chromaticStates.has(viewportId); }

// ══════════════════════════════════════════════════════════════
//  VIGNETTE
// ══════════════════════════════════════════════════════════════

const _vignetteStates = new Map();

/** @returns {boolean} получилось ли */
export function setVignette(viewportId, intensity = 0.4) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp?.element) return false;

  disableVignette(viewportId);

  const overlay = document.createElement('div');
  overlay.className = 'vignette-overlay';
  overlay.style.cssText = `
    position:absolute; inset:0; pointer-events:none; z-index:49;
    background:radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,${intensity}) 100%);
  `;
  vp.element.appendChild(overlay);
  _vignetteStates.set(viewportId, overlay);
  return true;
}

// [12.0.51] Выключение вынесено наружу: кнопка «Виньетка» стала переключателем
// (раньше каждое нажатие только пересоздавало затемнение — снять его было нечем).
export function disableVignette(viewportId) {
  const overlay = _vignetteStates.get(viewportId);
  if (overlay) { overlay.remove(); _vignetteStates.delete(viewportId); }
}

export function isVignetteOn(viewportId) { return _vignetteStates.has(viewportId); }

// ══════════════════════════════════════════════════════════════
//  ЦВЕТ ПО ПЛОТНОСТИ (HU)
// ══════════════════════════════════════════════════════════════
// [12.0.51] Было «Толщина→Цвет» / setThicknessColoring: подпись обещала цвет по
// ТОЛЩИНЕ (для MIP), а код ставил цветовую шкалу по ПЛОТНОСТИ в единицах
// Хаунсфилда — толщина не считалась нигде. Вернуть прежние цвета было нечем:
// шкала затирала цвета пресета до следующего выбора пресета. На МРТ шкала в HU
// бессмысленна. Теперь честное имя, переключатель с возвратом прежних цветов
// и отказ не на КТ.
const _цветБыл = new Map();   // viewportId → прежние узлы цветовой функции

/** Шкала «плотность → цвет» (HU). */
export const ШКАЛА_ПЛОТНОСТИ = Object.freeze([
  [-1024, 0, 0, 0.5],   // воздух — тёмно-синий
  [-500, 0, 0, 0.8],    // лёгкие — синий
  [0, 0, 0.5, 0.5],     // вода — голубой
  [50, 0, 0.8, 0],      // мягкие ткани — зелёный
  [200, 1, 0.8, 0],     // плотное — жёлтый
  [500, 1, 0.3, 0],     // кость — оранжевый
  [1000, 1, 0, 0],      // плотная кость — красный
  [3000, 1, 1, 1],      // металл — белый
]);

function _модальностьОкна(vp) {
  try {
    const volId = vp.getVolumeId?.() || vp.getActors?.()?.[0]?.referencedId || vp.getActors?.()?.[0]?.uid;
    const vol = volId ? cornerstone?.cache?.getVolume?.(volId) : null;
    const м = vol?.metadata?.Modality || vol?.metadata?.modality;
    if (м) return String(м).toUpperCase();
    const первый = vol?.imageIds?.[0];
    const мод = первый ? cornerstone?.metaData?.get?.('generalSeriesModule', первый)?.modality : null;
    return мод ? String(мод).toUpperCase() : null;
  } catch (e) { return null; }
}

export function isDensityColoringOn(viewportId) { return _цветБыл.has(viewportId); }

/**
 * @returns {{ok:boolean, причина?:string}}
 */
export function setDensityColoring(viewportId, enabled = true) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const cfun = (vp?.getDefaultActor?.()?.actor || vp?.getActors?.()?.[0]?.actor)?.getProperty?.()?.getRGBTransferFunction?.(0);
  if (!cfun?.removeAllPoints) return { ok: false, причина: 'объёмное окно не отдаёт цветовую функцию' };
  try {
    if (enabled) {
      const мод = _модальностьОкна(vp);
      if (мод && мод !== 'CT') return { ok: false, причина: `шкала в единицах Хаунсфилда — только для КТ, а здесь ${мод}` };
      if (!_цветБыл.has(viewportId)) {
        const узлы = [];
        for (let i = 0; i < cfun.getSize(); i++) { const б = [0, 0, 0, 0, 0, 0]; cfun.getNodeValue(i, б); узлы.push(б); }
        _цветБыл.set(viewportId, узлы);
      }
      cfun.removeAllPoints();
      for (const [hu, r, g, b] of ШКАЛА_ПЛОТНОСТИ) cfun.addRGBPoint(hu, r, g, b);
    } else {
      const узлы = _цветБыл.get(viewportId);
      _цветБыл.delete(viewportId);
      if (узлы?.length) {
        cfun.removeAllPoints();
        for (const [x, r, g, b, mid, sharp] of узлы) cfun.addRGBPointLong ? cfun.addRGBPointLong(x, r, g, b, mid, sharp) : cfun.addRGBPoint(x, r, g, b);
      }
    }
    vp.render();
    state.cinematicRenderer?.forceRender?.();
    return { ok: true };
  } catch (e) { log.warn('[Цвет по плотности]', e.message); return { ok: false, причина: e.message }; }
}

// ══════════════════════════════════════════════════════════════
//  SLICE MONTAGE
// ══════════════════════════════════════════════════════════════

/**
 * Создать монтаж из нескольких срезов в одном viewport'е.
 * @param {string} viewportId
 * @param {number[]} sliceIndices — индексы срезов для отображения
 * @param {Object} [opts]
 * @param {number} [opts.cols=4] — количество колонок
 * @param {number} [opts.gap=4] — промежуток между срезами в пикселях
 */
export async function createSliceMontage(viewportId, sliceIndices, opts = {}) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return;

  const cols = opts.cols || 4;
  const gap = opts.gap || 4;
  const rows = Math.ceil(sliceIndices.length / cols);

  const canvas = vp.canvas || vp.element?.querySelector('canvas');
  if (!canvas) return;

  // Сохраняем оригинальные срезы как изображения
  const images = [];
  const currentIdx = vp.type === ViewportType.STACK ? vp.getCurrentImageIdIndex?.() || 0 : vp.getSliceIndex?.() || 0;

  for (const idx of sliceIndices) {
    if (vp.type === ViewportType.STACK) {
      vp.setImageIdIndex(idx);
    } else {
      vp.scroll(idx - currentIdx);
    }
    vp.render();
    await new Promise(r => setTimeout(r, 100));

    const imgCanvas = document.createElement('canvas');
    imgCanvas.width = canvas.width / cols - gap;
    imgCanvas.height = canvas.height / rows - gap;
    const imgCtx = imgCanvas.getContext('2d');
    imgCtx.drawImage(canvas, 0, 0, imgCanvas.width, imgCanvas.height);
    images.push(imgCanvas);
  }

  // Возвращаемся к исходному срезу
  if (vp.type === ViewportType.STACK) {
    vp.setImageIdIndex(currentIdx);
  } else {
    vp.scroll(currentIdx - sliceIndices[sliceIndices.length - 1]);
  }
  vp.render();

  // Создаём монтажное изображение
  const montageCanvas = document.createElement('canvas');
  montageCanvas.width = canvas.width;
  montageCanvas.height = canvas.height;
  const mCtx = montageCanvas.getContext('2d');
  mCtx.fillStyle = '#000';
  mCtx.fillRect(0, 0, montageCanvas.width, montageCanvas.height);

  const cellW = (canvas.width - gap * (cols + 1)) / cols;
  const cellH = (canvas.height - gap * (rows + 1)) / rows;

  for (let i = 0; i < images.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = gap + col * (cellW + gap);
    const y = gap + row * (cellH + gap);
    mCtx.drawImage(images[i], x, y, cellW, cellH);
  }

  // Показываем монтаж как оверлей
  const overlay = document.createElement('div');
  overlay.className = 'montage-overlay';
  overlay.style.cssText = 'position:absolute;inset:0;z-index:100;background:#000;cursor:pointer;';
  overlay.appendChild(montageCanvas);
  montageCanvas.style.width = '100%';
  montageCanvas.style.height = '100%';
  overlay.addEventListener('click', () => overlay.remove());
  vp.element.appendChild(overlay);
}

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyAllEffects() {
  _wlAnimations.forEach(ac => ac.abort());
  _wlAnimations.clear();
  _slabStates.clear();
  resetClipping();
  _invertStates.clear();
  for (const vpId of _flashlightStates.keys()) disableFlashlight(vpId);
  for (const vpId of _chromaticStates.keys()) disableChromaticAberration(vpId);
  for (const vpId of _vignetteStates.keys()) disableVignette(vpId);
}